import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from "./services/api";
import {formatCurrency, formatRate} from "./CoreFunctions";
import FinancingPortfolioCharts from './components/financing/charts/FinancingPortfolioCharts.jsx';

const LoanList = () => {
    const [loans, setLoans] = useState([]); // Correctly destructured state
    const [revolvers, setRevolvers] = useState([]); // Correctly destructured state
    const [analytics, setAnalytics] = useState(null);
    const [range, setRange] = useState('12m'); // '6m' | '12m' | 'ytd' | 'all'
    const [viewMode, setViewMode] = useState('tiles'); // default to 'tiles'

    useEffect(() => {
        // Fetch the loans from the backend
        const fetchLoans = async () => {
            try {
                const response = await api.get('/balance_sheet/financing/');
                setLoans(response.data);
            } catch (error) {
                console.error("Error fetching loans:", error);
            }
        };

        fetchLoans();
    }, []); // Run on component mount

    useEffect(() => {
        // Fetch the loans from the backend
        const fetchRevolvers = async () => {
            try {
                const response = await api.get('/balance_sheet/financing/revolvers/');
                setRevolvers(response.data);
            } catch (error) {
                console.error("Error fetching revolvers:", error);
            }
        };

        fetchRevolvers();
    }, []); // Run on component mount

    // Fetch unified analytics for charts
    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                let params = {};
                const now = new Date();
                const end = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`;
                if (range === '6m' || range === '12m') {
                    const months = range === '6m' ? 6 : 12;
                    const startDate = new Date(now);
                    startDate.setMonth(startDate.getMonth() - (months - 1));
                    const start = `${startDate.getFullYear()}-${String(startDate.getMonth()+1).padStart(2,'0')}-01`;
                    params = { start_date: start, end_date: end, group_by: 'month' };
                } else if (range === 'ytd') {
                    const start = `${now.getFullYear()}-01-01`;
                    params = { start_date: start, end_date: end, group_by: 'month' };
                } else if (range === 'all') {
                    params = { group_by: 'month' };
                }
                const resp = await api.get('/balance_sheet/financing/analytics/', { params });
                setAnalytics(resp.data);
            } catch (e) {
                console.error('Error fetching financing analytics:', e);
            }
        };
        fetchAnalytics();
    }, [range]);

    const handleRemove = async (id, loanType) => {
        let deleteUrl = '';

        if (loanType === 'mortgage') {
            deleteUrl = `/balance_sheet/financing/mortgages/${id}/`; // URL for removing a mortgage
        } else if (loanType === 'auto_loan') {
            deleteUrl = `/balance_sheet/financing/auto_loans/${id}/`; // URL for removing an auto loan
        }

        try {
            await api.delete(deleteUrl); // Call the appropriate DELETE API
            setLoans((prevLoans) => prevLoans.filter(loan => loan.id !== id)); // Update the state
            alert("Loan removed successfully");
        } catch (error) {
            console.error("Error removing loan:", error);
            alert("There was an error removing the loan.");
        }
    };

    console.log('Loans:', loans)
    // Helper function to format loan amount as currency
    const formatCurrency = (amount) => {
        return `$${Math.round(amount).toLocaleString()}`;
    };


    // Collect data needed to calculate weighted average current interest rate on debt portfolio
    const outstandingAmountsArray = [];
    const currentRatesArray = [];

    loans.forEach(loan => {
      outstandingAmountsArray.push(parseFloat(loan.recent_payment.outstanding_balance));
      currentRatesArray.push(loan.current_rate);
    });

    revolvers.forEach(revolver => {
      outstandingAmountsArray.push(revolver.current_outstanding);
      currentRatesArray.push(revolver.current_rate);
    });

    console.log('outstandingAmountsArray:', outstandingAmountsArray)
    console.log('currentRatesArray:', currentRatesArray)

    const sumProduct = outstandingAmountsArray.reduce((acc, amt, index) => {
      return acc + amt * currentRatesArray[index];
    }, 0);

    const totalOutstanding = outstandingAmountsArray.reduce((acc, amt) => acc + amt, 0);

    const weightedAvgRate = totalOutstanding ? (sumProduct / totalOutstanding) : 0;

    // Helper: robust percent renderer that handles raw decimals (0.2402) and already-formatted strings ("24.02%")
    const renderPercent = (val, dp = 2) => {
        if (val == null) return 'n/a';
        const t = typeof val;
        if (t === 'number') return formatRate(val, dp); // decimal -> percent string
        // If it's a Money-like object with amount number (edge case), try to coerce
        if (t === 'object') {
            try {
                const num = Number(val.amount ?? val);
                if (Number.isFinite(num)) return formatRate(num, dp);
            } catch (e) { /* fall through */ }
        }
        // String: if it already contains a % sign, return as-is; otherwise try to parse and format
        if (t === 'string') {
            if (val.includes('%')) return val;
            const num = Number(val);
            if (Number.isFinite(num)) return formatRate(num, dp);
        }
        return String(val);
    };

    // Calculate total loan or credit limit amount, converting values to numbers
    const totalLoansAmount = loans.reduce((total, loan) => {
        return total + (parseFloat(loan.amount) || 0); // Convert to a float, default to 0 if NaN
    }, 0);
    const totalRevolverLimits = revolvers.reduce((total, revolver) => {
        return total + (parseFloat(revolver.current_credit_limit) || 0); // Convert to a float, default to 0 if NaN
    }, 0);

    const totalDebtLimits = totalLoansAmount + totalRevolverLimits

    // // Calculate total outstanding
    // const totalLoansOutstandingAmount = loans.reduce((total, loan) => {
    //     return total + (loan.recent_payment ? parseFloat(loan.recent_payment.outstanding_balance) || 0 : 0);
    // }, 0);
    // const totalRevolversOutstandingAmount = revolvers.reduce((total, revolver) => {
    //     return total + (revolver.recent_payment ? parseFloat(revolver.recent_payment) || 0 : 0);
    // }, 0);
    //
    // const totalDebtOutstandingAmount = totalLoansOutstandingAmount + totalRevolversOutstandingAmount

    // Total % Outstanding
    const percentageOutstanding = totalDebtLimits > 0
    ? ((totalOutstanding / totalDebtLimits) * 100).toFixed(2) // Calculate as percentage and format to 2 decimal places
    : '0.00';

    // Total principal paid (Loans only)
    const totalPrincipalPaid = loans.reduce((total, loan) => {
        return total + (loan.recent_payment ? parseFloat(loan.recent_payment.cumulative_principal_payment_amt) || 0 : 0);
    }, 0);

    // Total Interest Paid
    const totalLoanInterestPaid = loans.reduce((total, loan) => {
        return total + (loan.recent_payment ? parseFloat(loan.recent_payment.cumulative_interest_payment_amt) || 0 : 0);
    }, 0);
    const totalRevolverInterestPaid = revolvers.reduce((total, revolver) => {
        return total + (parseFloat(revolver.interest_paid_to_date));
    }, 0);

    const totalInterestPaid = totalLoanInterestPaid + totalRevolverInterestPaid

    const totalClosingCosts = loans.reduce((total, loan) => {
        return total + (parseFloat(loan.total_closing_costs) || 0); // Convert to a float, default to 0 if NaN
    }, 0);

    const totalInterestPaidPercentage = totalDebtLimits > 0
        ? ((totalInterestPaid / totalDebtLimits) * 100).toFixed(2) // Calculate as percentage and format to 2 decimal places
        : '0.00';

    const totalCostOfDebt = (totalInterestPaid != null && totalClosingCosts != null)
    ? (totalInterestPaid + totalClosingCosts).toFixed(2)
    : '0.00';

    const totalCostOfLoansPercentage = totalDebtLimits > 0
        ? ((totalCostOfDebt / totalDebtLimits) * 100).toFixed(2) // Calculate as percentage and format to 2 decimal places
        : '0.00';

    // Helper to build asset detail URL from loan + asset_info
    // Falls back to loan.debt_type when asset_info.type_2 is missing from API
    const buildAssetUrl = (loanObj) => {
        if (!loanObj || !loanObj.asset_info || !loanObj.asset_info.id) return null;
        const assetInfo = loanObj.asset_info;
        const id = assetInfo.id;
        let type2 = assetInfo.type_2 || assetInfo.type2 || assetInfo.asset_type_2;
        // Infer from loan type if backend omitted type_2
        if (!type2 && loanObj.debt_type) {
            if (loanObj.debt_type === 'mortgage') type2 = 'resi_property';
            if (loanObj.debt_type === 'auto_loan') type2 = 'vehicle';
        }
        switch (type2) {
            case 'vehicle':
                return `/balance_sheet/assets/physical_assets/vehicles/${id}`;
            case 'resi_property':
                return `/balance_sheet/assets/physical_assets/resi_properties/${id}`;
            // add more types as needed
            default:
                return null;
        }
    };

    const mortgages = loans.filter(l => l.debt_type === 'mortgage');
    const autoLoans = loans.filter(l => l.debt_type === 'auto_loan');
    const creditCards = revolvers.filter(r => r.revolver_type === 'credit_card');
    const helocs = revolvers.filter(r => r.revolver_type === 'heloc');

    const HeaderMetrics = ({ totals, isTicker }) => {
        return (
            <div className={`header-metrics ${isTicker ? 'ticker' : ''}`} style={isTicker ? { display:'flex', alignItems:'baseline', gap:24 } : { display:'grid', gridTemplateColumns: 'repeat(5, 180px)', gap:16, alignItems:'baseline' }}>
                <div className="header-metric">
                    <span className="metric-label">{isTicker ? 'Limit' : 'Total Limit/Amount'}</span>{' '}
                    <span className="metric-value">{formatCurrency(totals.limit || 0)}</span>
                </div>
                <div className="header-metric">
                    <span className="metric-label">{isTicker ? 'Rate' : 'Weighted Avg Rate'}</span>{' '}
                    <span className="metric-value">{formatRate(totals.avgRate || 0, 2)}</span>
                </div>
                <div className="header-metric">
                    <span className="metric-label">{isTicker ? 'Outstanding' : 'Total Outstanding'}</span>{' '}
                    <span className="metric-value">
                        {formatCurrency(totals.outstanding || 0)}
                        <span style={{ fontSize: '0.85em', marginLeft: 4, color: '#9ca3af' }}>
                            ({totals.limit > 0 ? ((totals.outstanding / totals.limit) * 100).toFixed(1) : 0}%)
                        </span>
                    </span>
                </div>
                <div className="header-metric">
                    <span className="metric-label">{isTicker ? 'Interest' : 'Interest Paid'}</span>{' '}
                    <span className="metric-value" style={{ color: '#f87171' }}>{formatCurrency(totals.interestPaid || 0)}</span>
                </div>
                <div className="header-metric">
                    <span className="metric-label">Total Cost</span>{' '}
                    <span className="metric-value" style={{ color: '#f87171' }}>{formatCurrency(totals.totalCost || 0)}</span>
                </div>
            </div>
        );
    };

    const buildGroupTotals = (groupLoans, groupRevolvers) => {
        const outstandingAmountsArray = [];
        const currentRatesArray = [];
        let limit = 0;
        let interestPaid = 0;
        let totalCost = 0;

        groupLoans.forEach(loan => {
            const out = parseFloat(loan.recent_payment?.outstanding_balance) || 0;
            outstandingAmountsArray.push(out);
            currentRatesArray.push(loan.current_rate);
            limit += parseFloat(loan.amount) || 0;
            interestPaid += parseFloat(loan.recent_payment?.cumulative_interest_payment_amt) || 0;
            totalCost += parseFloat(loan.recent_payment?.total_cost_of_loan_to_date_amt) || 0;
        });

        groupRevolvers.forEach(revolver => {
            const out = revolver.current_outstanding || 0;
            outstandingAmountsArray.push(out);
            currentRatesArray.push(revolver.current_rate);
            limit += parseFloat(revolver.current_credit_limit) || 0;
            interestPaid += parseFloat(revolver.interest_paid_to_date) || 0;
            totalCost += parseFloat(revolver.total_cost_paid_to_date) || 0;
        });

        const sumProduct = outstandingAmountsArray.reduce((acc, amt, index) => acc + amt * currentRatesArray[index], 0);
        const outstanding = outstandingAmountsArray.reduce((acc, amt) => acc + amt, 0);
        const avgRate = outstanding ? (sumProduct / outstanding) : 0;

        return { limit, outstanding, avgRate, interestPaid, totalCost };
    };

    const overallTotals = buildGroupTotals(loans, revolvers);

    return (
        <div className="page-container" style={{ marginLeft: '40px' }}>
            <div className="page-header-row" style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', rowGap: 8, marginBottom: '20px', width: '100%', justifyContent: 'flex-start', minWidth: 0 }}>
                <div className="header-title-container" style={{ flexShrink: 1, minWidth: 0 }}>
                    <h1 style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Debt</h1>
                </div>
                {viewMode === 'tiles' && (
                    <div className="header-ticker" style={{ display: 'flex', alignItems: 'baseline', borderLeft: '1px solid #444', paddingLeft: '24px', marginLeft: '12px' }}>
                        <HeaderMetrics totals={overallTotals} isTicker={true} />
                    </div>
                )}
                <div className="header-breadcrumb-container" style={{ flexShrink: 0, marginLeft: 'auto', marginRight: '40px' }}>
                    <Link to="/balance_sheet/assets/" style={{ whiteSpace: 'nowrap' }}> Assets</Link>
                </div>
            </div>

            <div className="tabs-row" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 12, width: '100%', padding: '0 20px', boxSizing: 'border-box', marginBottom: '20px' }}>
                <div className="view-tabs" style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                    <div className="tab-bar">
                        <button className={`tab-btn ${viewMode === 'tiles' ? 'active' : ''}`} onClick={() => setViewMode('tiles')}>Tiles</button>
                        <button className={`tab-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')}>Table</button>
                    </div>
                    <Link to="/balance_sheet/financing/create-loan/">
                        <button type="button" className="btn btn-primary" style={{ fontSize: '14px', height: '32px', padding: '0 12px', display: 'flex', alignItems: 'center', backgroundColor: '#007bff' }}>
                            Add New Debt
                        </button>
                    </Link>
                </div>
            </div>

            {viewMode === 'table' ? (
                <>
                    <table className="debt-table">
                        <thead>
                            <tr>
                                <th>Loan ID</th>
                                <th>Debt Type</th>
                                <th>Lender</th>
                                <th>Loan or Line Amount</th>
                                <th>Rate Type</th>
                                <th>Current Rate</th>
                                <th>Outstanding Amt.</th>
                                <th>% Outstanding</th>
                                <th>Principal Paid</th>
                                <th>Interest Paid To-Date</th>
                                <th>Interest Paid % of Loan Amt.</th>
                                <th>Total Closing Costs & Fees Paid</th>
                                <th>Total Cost of Loan (To-Date)</th>
                                <th>Total Cost of Loan (To-Date) % of Loan Amt.</th>
                                <th>Linked Asset</th>
                                <th>Maturity Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(loans.length === 0 && revolvers.length === 0) ? (
                              <tr>
                                <td colSpan="5" style={{ textAlign: 'center' }}>No loans or revolvers found.</td>
                              </tr>
                            ) : (
                              <>
                                {loans.map(loan => (
                                  <tr key={loan.id}>
                                    <td>
                                        <Link to={`${loan.debt_type}s/${loan.id}`}>{loan.id}</Link>
                                    </td>
                                    <td>
                                        <Link to={`${loan.debt_type}s`}>{loan.debt_type}</Link>
                                    </td>
                                    <td>{loan.lender_name}</td>
                                    <td>{formatCurrency(loan.amount)}</td>
                                    <td>{loan.rate_type}</td>
                                    <td>{formatRate(loan.current_rate, 2)}</td>
                                    <td>{loan.recent_payment ? formatCurrency(loan.recent_payment.outstanding_balance) : 'n/a'}</td>
                                    <td>{loan.recent_payment ? renderPercent(loan.recent_payment.percent_outstanding, 2) : 'n/a'}</td>
                                    <td>{loan.recent_payment ? formatCurrency(loan.recent_payment.cumulative_principal_payment_amt) : 'n/a'}</td>
                                    <td>{loan.recent_payment ? formatCurrency(loan.recent_payment.cumulative_interest_payment_amt) : 'n/a'}</td>
                                    <td>{loan.recent_payment ? renderPercent(loan.recent_payment.interest_paid_as_perc_of_loan_amt, 2) : 'n/a'}</td>
                                    <td>{formatCurrency(loan.total_closing_costs)}</td>
                                    <td style={{ color: '#f87171' }}>{loan.recent_payment ? formatCurrency(loan.recent_payment.total_cost_of_loan_to_date_amt) : 'n/a'}</td>
                                    <td>{loan.recent_payment ? renderPercent(loan.recent_payment.total_cost_of_loan_to_date_perc, 2) : 'n/a'}</td>
                                    <td>
                                        {(() => {
                                            const url = buildAssetUrl(loan);
                                            const label = loan.asset_info?.description;
                                            return (url && label) ? (
                                                <Link to={url}>{label}</Link>
                                            ) : (
                                                <span></span>
                                            );
                                        })()}
                                    </td>
                                    <td>{loan.maturity_date || 'n/a'}</td>
                                    <td>
                                        <button type="button" className="btn btn-danger" onClick={() => handleRemove(loan.id, loan.debt_type)}>Remove</button>
                                    </td>
                                  </tr>
                                ))}
                                {revolvers.map(revolver => (
                                  <tr key={revolver.id}>
                                        <td>
                                            <Link to={`revolvers/${revolver.revolver_type.replace(/_/g, '-')}s/${revolver.id}/`}>{revolver.id}</Link>
                                        </td>
                                        <td>
                                            <Link to={`revolvers/${revolver.revolver_type.replace(/_/g, '-')}s`}>{revolver.revolver_type.replace(/_/g, '-')}</Link>
                                        </td>
                                    <td>{revolver.lender_name}</td>
                                    <td>{formatCurrency(revolver.current_credit_limit)}</td>
                                    <td>Floating</td>
                                    <td>{formatRate(revolver.current_rate, 2)}</td>
                                    <td>{formatCurrency(revolver.current_outstanding)}</td>
                                    <td>{formatRate(revolver.percent_outstanding, 2)}</td>
                                    <td>n/a</td>
                                    <td>{revolver.interest_paid_to_date ? formatCurrency(revolver.interest_paid_to_date) : 0}</td>
                                    <td>{revolver.interest_paid_as_perc_of_loan_amt ? `${formatRate(revolver.interest_paid_as_perc_of_loan_amt, 2)}` : 'n/a'}</td>
                                    <td>{revolver.fees_paid_to_date ? formatCurrency(revolver.fees_paid_to_date) : 0}</td>
                                    <td style={{ color: '#f87171' }}>{revolver.total_cost_paid_to_date ? formatCurrency(revolver.total_cost_paid_to_date) : 0}</td>
                                    <td>{revolver.total_cost_paid_as_perc_of_loan_amt ? `${formatRate(revolver.total_cost_paid_as_perc_of_loan_amt, 2)}` : 'n/a'}</td>
                                    <td>
                                        {(() => {
                                            const url = buildAssetUrl(revolver);
                                            const label = revolver.asset_info?.description;
                                            return (url && label) ? (
                                                <Link to={url}>{label}</Link>
                                            ) : (
                                                <span></span>
                                            );
                                        })()}
                                    </td>
                                    <td>{revolver.maturity_date || 'n/a'}</td>
                                    <td>
                                        <button type="button" className="btn btn-danger" onClick={() => handleRemove(revolver.id, 'revolvers')}>Remove</button>
                                    </td>
                                  </tr>
                                ))}
                              </>
                            )}

                            <tr className="total-row">
                                <td><strong>Total</strong></td>
                                <td></td>
                                <td></td>
                                <td><strong>{formatCurrency(totalDebtLimits)}</strong></td>
                                <td></td>
                                <td><strong>{formatRate(weightedAvgRate, 2)}</strong></td>
                                <td><strong>{formatCurrency(totalOutstanding)}</strong></td>
                                <td><strong>{percentageOutstanding}%</strong></td>
                                <td><strong>{formatCurrency(totalPrincipalPaid)}</strong></td>
                                <td><strong>{formatCurrency(totalInterestPaid)}</strong></td>
                                <td><strong>{totalInterestPaidPercentage}%</strong></td>
                                <td><strong>{formatCurrency(totalClosingCosts)}</strong></td>
                                <td style={{ color: '#f87171' }}><strong>{formatCurrency(totalCostOfDebt)}</strong></td>
                                <td><strong>{totalCostOfLoansPercentage}%</strong></td>
                                <td></td>
                                <td></td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Charts Section */}
                    <div style={{ marginTop: '24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <h3 style={{ marginRight: '12px' }}>Debt Portfolio Charts</h3>
                            <div>
                                <button className={`btn ${range==='6m' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setRange('6m')}>6M</button>
                            </div>
                            <div>
                                <button className={`btn ${range==='12m' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setRange('12m')}>1Y</button>
                            </div>
                            <div>
                                <button className={`btn ${range==='ytd' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setRange('ytd')}>YTD</button>
                            </div>
                            <div>
                                <button className={`btn ${range==='all' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setRange('all')}>All</button>
                            </div>
                        </div>
                        <FinancingPortfolioCharts data={analytics} />
                    </div>
                </>
            ) : (
                <div className="tiles-view" style={{ width: '100%', padding: '0 20px 40px 20px' }}>
                    {/* Mortgages Section */}
                    <div className="tiles-section-header">
                        <div className="header-title-wrap">
                            <h2 className="tiles-section-title">Mortgages</h2>
                        </div>
                        <HeaderMetrics totals={buildGroupTotals(mortgages, [])} />
                    </div>
                    <div className="tiles-grid">
                        {mortgages.map(loan => (
                            <Link key={loan.id} to={`mortgages/${loan.id}`} className="tile-card-link">
                                <div className="tile-card">
                                    {loan.asset_info?.photo_thumb ? (
                                        <img src={`${loan.asset_info.photo_thumb}`} alt="Mortgage Asset" className="tile-img" loading="lazy" />
                                    ) : (
                                        <div className="tile-icon">🏠</div>
                                    )}
                                    <div className="tile-title">{loan.lender_name} Mortgage</div>
                                    <div className="tile-subtitle">{loan.asset_info?.description || `Loan #${loan.id}`}</div>
                                    <div className="tile-metrics">
                                        <div>
                                            <span className="metric-label">Outstanding</span>
                                            <span className="metric-value">{loan.recent_payment ? formatCurrency(loan.recent_payment.outstanding_balance) : 'n/a'}</span>
                                        </div>
                                        <div>
                                            <span className="metric-label">Rate</span>
                                            <span className="metric-value">{formatRate(loan.current_rate, 2)}</span>
                                        </div>
                                        <div>
                                            <span className="metric-label">Interest Paid</span>
                                            <span className="metric-value" style={{ color: '#f87171' }}>{loan.recent_payment ? formatCurrency(loan.recent_payment.cumulative_interest_payment_amt) : 'n/a'}</span>
                                        </div>
                                        <div>
                                            <span className="metric-label">Total Cost</span>
                                            <span className="metric-value" style={{ color: '#f87171' }}>{loan.recent_payment ? formatCurrency(loan.recent_payment.total_cost_of_loan_to_date_amt) : 'n/a'}</span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {/* Auto Loans Section */}
                    <div className="tiles-section-header" style={{ marginTop: '40px' }}>
                        <div className="header-title-wrap">
                            <h2 className="tiles-section-title">Auto Loans</h2>
                        </div>
                        <HeaderMetrics totals={buildGroupTotals(autoLoans, [])} />
                    </div>
                    <div className="tiles-grid">
                        {autoLoans.map(loan => (
                            <Link key={loan.id} to={`auto_loans/${loan.id}`} className="tile-card-link">
                                <div className="tile-card">
                                    {loan.asset_info?.photo_thumb ? (
                                        <img src={`${loan.asset_info.photo_thumb}`} alt="Auto Loan Asset" className="tile-img" loading="lazy" />
                                    ) : (
                                        <div className="tile-icon">🚗</div>
                                    )}
                                    <div className="tile-title">{loan.lender_name} Auto Loan</div>
                                    <div className="tile-subtitle">{loan.asset_info?.description || `Loan #${loan.id}`}</div>
                                    <div className="tile-metrics">
                                        <div>
                                            <span className="metric-label">Outstanding</span>
                                            <span className="metric-value">{loan.recent_payment ? formatCurrency(loan.recent_payment.outstanding_balance) : 'n/a'}</span>
                                        </div>
                                        <div>
                                            <span className="metric-label">Rate</span>
                                            <span className="metric-value">{formatRate(loan.current_rate, 2)}</span>
                                        </div>
                                        <div>
                                            <span className="metric-label">Interest Paid</span>
                                            <span className="metric-value" style={{ color: '#f87171' }}>{loan.recent_payment ? formatCurrency(loan.recent_payment.cumulative_interest_payment_amt) : 'n/a'}</span>
                                        </div>
                                        <div>
                                            <span className="metric-label">Total Cost</span>
                                            <span className="metric-value" style={{ color: '#f87171' }}>{loan.recent_payment ? formatCurrency(loan.recent_payment.total_cost_of_loan_to_date_amt) : 'n/a'}</span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {/* Revolving Debt Section (HELOCs and Credit Cards) */}
                    <div className="tiles-section-header" style={{ marginTop: '40px' }}>
                        <div className="header-title-wrap">
                            <h2 className="tiles-section-title">Revolving Debt</h2>
                        </div>
                        <HeaderMetrics totals={buildGroupTotals([], [...creditCards, ...helocs])} />
                    </div>
                    <div className="tiles-grid">
                        {[...helocs, ...creditCards].map(revolver => (
                            <Link key={revolver.id} to={`revolvers/${revolver.revolver_type.replace(/_/g, '-')}s/${revolver.id}/`} className="tile-card-link">
                                <div className="tile-card">
                                    {revolver.asset_info?.photo_thumb ? (
                                        <img src={`${revolver.asset_info.photo_thumb}`} alt="Revolver Asset" className="tile-img" loading="lazy" />
                                    ) : (
                                        <div className="tile-icon">{revolver.revolver_type === 'heloc' ? '🏦' : '💳'}</div>
                                    )}
                                    <div className="tile-title">{revolver.lender_name} {revolver.revolver_type === 'heloc' ? 'HELOC' : 'Credit Card'}</div>
                                    <div className="tile-subtitle">{revolver.asset_info?.description || `Account last 4: ${revolver.account_last4 || 'n/a'}`}</div>
                                    <div className="tile-metrics">
                                        <div>
                                            <span className="metric-label">Outstanding</span>
                                            <span className="metric-value">{formatCurrency(revolver.current_outstanding)}</span>
                                        </div>
                                        <div>
                                            <span className="metric-label">Rate</span>
                                            <span className="metric-value">{formatRate(revolver.current_rate, 2)}</span>
                                        </div>
                                        <div>
                                            <span className="metric-label">Interest Paid</span>
                                            <span className="metric-value" style={{ color: '#f87171' }}>{formatCurrency(revolver.interest_paid_to_date)}</span>
                                        </div>
                                        <div>
                                            <span className="metric-label">Total Cost</span>
                                            <span className="metric-value" style={{ color: '#f87171' }}>{formatCurrency(revolver.total_cost_paid_to_date)}</span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            <style jsx>{`
                .page-container {
                    margin: 20px;
                    width: 90%;
                    table-layout: auto;
                    border-collapse: collapse;
                }
                .debt-table {
                    width: 100%;
                    border-collapse: collapse;
                }
                .debt-table th, .debt-table td {
                    border: 1px solid #ccc;
                    padding: 10px;
                    text-align: left;
                }
                .debt-table th {
                    background-color: #f0f0f0;
                }
                .total-row td {
                    border: none; /* Remove borders for the total row */
                }
                /* Tabs / Buttons Styling */
                .tab-bar {
                    display: inline-flex;
                    align-items: flex-end;
                    gap: 4px;
                    border-bottom: 1px solid #374151;
                }
                .view-tabs .tab-btn {
                    background: #111827;
                    color: #e5e7eb;
                    border: 1px solid #374151;
                    border-bottom-color: #374151;
                    padding: 6px 14px;
                    border-top-left-radius: 8px;
                    border-top-right-radius: 8px;
                    cursor: pointer;
                    font-size: 14px;
                }
                .view-tabs .tab-btn:hover { background: #0f172a; }
                .view-tabs .tab-btn.active {
                    background: #0b1220;
                    border-color: #4b5563;
                    border-bottom-color: transparent;
                    color: #ffffff;
                    position: relative;
                    top: 1px;
                }
                .tiles-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
                    gap: 20px;
                    padding: 18px 0 10px 0;
                }
                .tile-card-link {
                    text-decoration: none;
                    color: inherit;
                }
                .tile-card {
                    border: 1px solid #2a2a2a;
                    border-radius: 10px;
                    padding: 18px;
                    background: #1e1e1e;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.3);
                    transition: box-shadow 0.15s ease, transform 0.15s ease;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                }
                .tile-card:hover {
                    box-shadow: 0 6px 18px rgba(0,0,0,0.35);
                    transform: translateY(-2px);
                }
                .tile-icon { font-size: 32px; margin-bottom: 8px; }
                .tile-img {
                    width: 100%;
                    height: 180px;
                    object-fit: cover;
                    border-radius: 8px;
                    margin-bottom: 12px;
                }
                .tile-title { font-weight: 700; font-size: 18px; color: #e6e6e6; }
                .tile-subtitle { color: #aaaaaa; font-size: 14px; margin-bottom: 12px; }
                .tile-metrics {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 12px;
                    font-size: 14px;
                }
                .metric-label { display: block; color: #bbbbbb; font-size: 12px; }
                .metric-value { display: block; font-weight: 600; color: #ffffff; }
                .tiles-section-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid #444;
                    padding-bottom: 8px;
                    margin-top: 20px;
                }
                .tiles-section-title { font-size: 24px; color: #fff; margin: 0; }
                .header-metrics { display: flex; gap: 24px; }
                .header-metric { text-align: right; }
                .header-ticker .header-metric { text-align: left; }
                .header-ticker .metric-label { font-size: 12px; color: #aaa; }
                .header-ticker .metric-value { font-size: 18px; color: #fff; }
                .btn-primary {
                    background-color: #007bff;
                    color: white;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 4px;
                    cursor: pointer;
                }
                .btn-danger {
                    background-color: #ff4d4d;
                    color: white;
                    border: none;
                    padding: 5px 10px;
                    cursor: pointer;
                }
            `}</style>
        </div>
    );
};

export default LoanList;